import { c as _c } from "react/compiler-runtime";
import { Icon } from '@/components/Atoms/Icon/Icon';
import { useDropdown } from '@/components/hooks/useDropdown';
import classNames from '@/utils/classNames';
import ChevronDown from '@bloomberg/web-icons-react/ChevronDown';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { FCC } from 'react-fc';
import { ToolbarControl } from './ToolbarControl';
import styles from './ToolbarDropdown.module.css';
import { ToolbarIconControl } from './ToolbarIconControl';
interface IToolbarDropdownProps {
  icon?: ReactNode;
  label: string;
  disabled?: boolean;
}
export const ToolbarDropdownPopover: FCC<IToolbarDropdownProps> = t0 => {
  const $ = _c(33);
  let children;
  let className;
  let disabled;
  let icon;
  let label;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      icon,
      label,
      disabled,
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = icon;
    $[5] = label;
    $[6] = rest;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    icon = $[4];
    label = $[5];
    rest = $[6];
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      closeOnClick: false
    };
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const {
    buttonRef,
    popoverRef,
    dropdownId,
    buttonId,
    isOpen
  } = useDropdown(t1);
  let t2;
  if ($[8] !== className) {
    t2 = classNames(styles.container, className);
    $[8] = className;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== icon || $[11] !== isOpen || $[12] !== label) {
    t3 = <TriggerContent icon={icon} label={label} expanded={isOpen} />;
    $[10] = icon;
    $[11] = isOpen;
    $[12] = label;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== buttonId || $[15] !== buttonRef || $[16] !== disabled || $[17] !== dropdownId || $[18] !== isOpen || $[19] !== label || $[20] !== t3) {
    t4 = <ToolbarControl id={buttonId} ref={buttonRef} type="button" disabled={disabled} popoverTarget={dropdownId} aria-expanded={isOpen} title={label} className={styles.popoverTrigger}>{t3}</ToolbarControl>;
    $[14] = buttonId;
    $[15] = buttonRef;
    $[16] = disabled;
    $[17] = dropdownId;
    $[18] = isOpen;
    $[19] = label;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classNames(styles.popover, styles.dropdownContent);
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== buttonId || $[24] !== children || $[25] !== dropdownId || $[26] !== popoverRef) {
    t6 = <div id={dropdownId} ref={popoverRef} className={t5} popover="auto" role="menu" aria-labelledby={buttonId}>{children}</div>;
    $[23] = buttonId;
    $[24] = children;
    $[25] = dropdownId;
    $[26] = popoverRef;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== rest || $[29] !== t2 || $[30] !== t4 || $[31] !== t6) {
    t7 = <div className={t2} {...rest}>{t4}{t6}</div>;
    $[28] = rest;
    $[29] = t2;
    $[30] = t4;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
};
export const ToolbarDropdownEmbedded: FCC<IToolbarDropdownProps> = t0 => {
  const $ = _c(13);
  const {
    icon,
    label,
    disabled,
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsOpen(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== icon || $[2] !== isOpen || $[3] !== label) {
    t2 = <TriggerContent icon={icon} label={label} expanded={isOpen} />;
    $[1] = icon;
    $[2] = isOpen;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== disabled || $[6] !== isOpen || $[7] !== label || $[8] !== t2) {
    t3 = <ToolbarControl type="button" disabled={disabled} onClick={t1} aria-expanded={isOpen} title={label}>{t2}</ToolbarControl>;
    $[5] = disabled;
    $[6] = isOpen;
    $[7] = label;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = isOpen && children;
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
const TriggerContent = t0 => {
  const $ = _c(4);
  const {
    icon,
    label,
    expanded
  } = t0;
  let t1;
  if ($[0] !== expanded || $[1] !== icon || $[2] !== label) {
    t1 = icon ? <ToolbarIconControl icon={icon} label={label} /> : <span className={styles.fallbackLabel}><span>{label}</span><span className={classNames(styles.chevron, expanded && styles.chevronOpen)}><Icon use={<ChevronDown />} /></span></span>;
    $[0] = expanded;
    $[1] = icon;
    $[2] = label;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
function _temp(prev) {
  return !prev;
}